import { useMemo } from "react";

import type { MonitorAndProtectModelCompetitorsApiModelCompetitorTableRow } from "@similarweb/pro-api-client";
import { filterFactory } from "@similarweb/pro-logic-components";

import { Favicon } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/layout/favicon";
import { useTranslation } from "pro-features/components/WithTranslation";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

export interface ICompetitorsFilterState {
    competitors?: string[];
}

const Filters = filterFactory.createTypedFilters<ICompetitorsFilterState>();

interface ICompetitorsDataFiltersProps {
    competitorsData: MonitorAndProtectModelCompetitorsApiModelCompetitorTableRow[];
    filterState: ICompetitorsFilterState;
    onFilterChange: (newState: ICompetitorsFilterState) => void;
    onFilterClick?: (filterName: string) => void;
    onFilterChangeEvent?: (filterName: string) => void;
}

export const CompetitorsDataFilters = ({
    competitorsData,
    filterState,
    onFilterChange,
    onFilterClick,
    onFilterChangeEvent,
}: ICompetitorsDataFiltersProps) => {
    const translate = useTranslation();

    const competitorOptions = useMemo(
        () =>
            competitorsData.map((competitor) => ({
                id: competitor.competitor,
                value: competitor.competitor,
                label: competitor.competitor,
                icon: (
                    <Favicon
                        iconUrl={getFaviconUrl(competitor.competitor)}
                        alt={competitor.competitor}
                    />
                ),
            })),
        [competitorsData],
    );

    const competitorsTooltip = useMemo(
        () =>
            filterState.competitors && filterState.competitors.length > 0
                ? filterState.competitors.join(", ")
                : undefined,
        [filterState.competitors],
    );

    return (
        <Filters.Provider
            state={filterState}
            onChange={onFilterChange}
            events={{
                onFilterClick: (filterName: string) => {
                    onFilterClick?.(filterName);
                },
                onFilterChange: (filterName: string) => {
                    onFilterChangeEvent?.(filterName);
                },
            }}
        >
            <Filters.MultiSelect
                name="competitors"
                placeholder={translate("monitor_and_protect.page.keywords.filters.competitors.placeholder")}
                options={competitorOptions}
                tooltip={competitorsTooltip}
                label={(value) => {
                    const competitorsLabel = translate("monitor_and_protect.page.keywords.filters.competitors.title");
                    return value && value.length > 0 ? `${competitorsLabel}: ${value.length}` : competitorsLabel;
                }}
            />
        </Filters.Provider>
    );
};
